import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { HttpService } from 'src/app/core/services/http.service';
import { ICustomerResponseDto } from 'src/app/features/customers/models/customer.models';
import { CustomersService } from 'src/app/features/customers/services/customers.service';
import { ICustomerOrderResponseDto } from '../../orders/models/order.models';
import { CustomerOrdersService } from '../../orders/services/customer-orders.service';
import {
    BLOCKABLE_ENTITY_TYPES,
    BlockableEntityType,
    IBlockRequestDto,
    IEntityBlockListResponse,
    IEntityBlockRecord,
    IEntityBlockRequest,
    IEntityBlockUpdateDto,
    IEntityBlockUpdateRequest,
} from '../models/entity-block.models';

@Injectable({ providedIn: 'root' })
export class EntityBlockService {
    private readonly httpService = inject(HttpService);
    private readonly apiService = inject(ApiService);
    private readonly customersService = inject(CustomersService);
    private readonly customerOrdersService = inject(CustomerOrdersService);

    getEntityBlocks(entityType: BlockableEntityType, entityId: string): Observable<IEntityBlockRecord[]> {
        const filter = `$filter=entityType eq '${entityType}' and entityId eq ${entityId}`;
        const url = this.apiService.pathUrl.entityBlocks(`?${filter}`);

        return this.httpService.get$<IEntityBlockListResponse>(url).pipe(map(response => response.value ?? []));
    }

    blockEntity(request: IEntityBlockRequest): Observable<ICustomerResponseDto | ICustomerOrderResponseDto> {
        if (!BLOCKABLE_ENTITY_TYPES.includes(request.entityType)) {
            throw new Error(`Unsupported entity type: ${request.entityType}`);
        }

        const payload: IBlockRequestDto = {
            entityType: request.entityType,
            entityId: request.entityId,
            reason: request.reason,
            description: request.description?.trim() || null,
        };

        return this.httpService
            .post$<void>(this.apiService.pathUrl.blockEntity(), payload, new HttpParams())
            .pipe(switchMap(() => this.reloadEntity(request.entityType, request.entityId)));
    }

    updateEntityBlock(request: IEntityBlockUpdateRequest): Observable<ICustomerResponseDto | ICustomerOrderResponseDto> {
        const payload: IEntityBlockUpdateDto = {
            reason: request.reason,
            description: request.description?.trim() || null,
        };

        return this.httpService
            .put$<void>(this.apiService.pathUrl.entityBlockById(request.id), payload)
            .pipe(switchMap(() => this.reloadEntity(request.entityType, request.entityId)));
    }

    deleteEntityBlock(
        blockId: string,
        entityType: BlockableEntityType,
        entityId: string
    ): Observable<ICustomerResponseDto | ICustomerOrderResponseDto> {
        return this.httpService
            .delete$<void>(this.apiService.pathUrl.entityBlockById(blockId))
            .pipe(switchMap(() => this.reloadEntity(entityType, entityId)));
    }

    private reloadEntity(entityType: BlockableEntityType, entityId: string): Observable<ICustomerResponseDto | ICustomerOrderResponseDto> {
        switch (entityType) {
            case BlockableEntityType.Customer:
                return this.customersService.getCustomerById(entityId);

            case BlockableEntityType.CustomerOrder:
                return this.customerOrdersService.getCustomerOrderById(entityId);

            default:
                throw new Error(`Unsupported entity type: ${entityType}`);
        }
    }
}
